import React, { useEffect, useRef, useState } from 'react';
import type { AdminApiClient, JsonRecord } from './api-client';

export function orderSelectionBlocked(order: JsonRecord): string {
  if (order.canManageDeletion !== true) return '无订单管理权限';
  if (order.adminDeletedAt) return '';
  if (!['PAYMENT_CLOSED', 'CANCELLED'].includes(String(order.status)))
    return '仅已关闭或已取消的订单可删除';
  return typeof order.adminDeletionBlockedReason === 'string'
    ? order.adminDeletionBlockedReason
    : '';
}

export function OrderBatchActions({
  api,
  appId,
  orders,
  selected,
  onSelect,
  deleted,
  onDeleted,
  onDone,
}: {
  api: AdminApiClient;
  appId: string;
  orders: JsonRecord[];
  selected: string[];
  onSelect: (ids: string[]) => void;
  deleted: boolean;
  onDeleted: (deleted: boolean) => void;
  onDone: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState(false);
  const firstInput = useRef<HTMLTextAreaElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const eligible = orders
    .filter((order) => !orderSelectionBlocked(order))
    .map((order) => String(order.id))
    .slice(0, 50);
  const allSelected = eligible.length > 0 && eligible.every((id) => selected.includes(id));
  const selectedOrders = orders.filter((order) => selected.includes(String(order.id)));
  useEffect(() => {
    if (confirming) firstInput.current?.focus();
  }, [confirming]);
  function close() {
    if (inFlight.current) return;
    setConfirming(false);
    opener.current?.focus();
  }
  async function submit() {
    if (inFlight.current || !reason.trim() || !selectedOrders.length) return;
    inFlight.current = true;
    setBusy(true);
    setFeedback('');
    try {
      await api.request(`/api/admin/m4/orders/batch-deletion?appId=${encodeURIComponent(appId)}`, {
        method: 'POST',
        body: JSON.stringify({
          orders: selectedOrders.map((order) => ({ id: order.id, version: order.version })),
          deleted: !deleted,
          reason: reason.trim(),
        }),
      });
      setError(false);
      setFeedback(
        `已${deleted ? '恢复' : '删除'} ${selectedOrders.length} 个订单${deleted ? '' : '，可在“已删除”中恢复'}。`,
      );
      setConfirming(false);
      setReason('');
      onSelect([]);
      onDone();
    } catch (err) {
      setError(true);
      setFeedback(err instanceof Error ? err.message : '操作未完成，请刷新后重试');
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  return (
    <>
      <div className="order-batch-bar">
        <div className="order-commands">
          <button
            type="button"
            aria-pressed={!deleted}
            disabled={busy}
            onClick={() => {
              onSelect([]);
              setFeedback('');
              onDeleted(false);
            }}
          >
            订单列表
          </button>
          <button
            type="button"
            aria-pressed={deleted}
            disabled={busy}
            onClick={() => {
              onSelect([]);
              setFeedback('');
              onDeleted(true);
            }}
          >
            已删除
          </button>
          <label>
            <input
              type="checkbox"
              aria-label="选择当前结果中可操作订单"
              disabled={!eligible.length || busy}
              checked={allSelected}
              onChange={() => onSelect(allSelected ? [] : eligible)}
            />
            全选可操作项（最多 50 条）
          </label>
          <span>已选 {selected.length} 项</span>
        </div>
        <button
          ref={opener}
          type="button"
          className={deleted ? '' : 'order-danger'}
          disabled={!selectedOrders.length || busy}
          onClick={() => {
            setFeedback('');
            setConfirming(true);
          }}
        >
          {deleted ? '恢复选中订单' : '删除选中订单'}
        </button>
      </div>
      <p className="order-hint">
        {deleted
          ? '仅从后台工作列表移除，客户订单、账务和操作记录均保留；恢复不会重新开启支付。'
          : '仅支持删除已关闭 / 已取消、无支付及售后记录的订单。待支付订单须先完成关单，不直接删除。'}
      </p>
      {feedback && !confirming && (
        <p role="status" className={`order-feedback ${error ? 'error' : ''}`}>
          {feedback}
        </p>
      )}
      {confirming && (
        <div className="order-dialog-backdrop">
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="order-delete-title"
            className="order-dialog"
            onKeyDown={(event) => {
              if (event.key === 'Escape') close();
              if (event.key === 'Tab') {
                const controls = Array.from(
                  event.currentTarget.querySelectorAll<HTMLElement>(
                    'textarea,button:not(:disabled)',
                  ),
                );
                const first = controls[0],
                  last = controls.at(-1);
                if (event.shiftKey && document.activeElement === first) {
                  event.preventDefault();
                  last?.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                  event.preventDefault();
                  first?.focus();
                }
              }
            }}
          >
            <h2 id="order-delete-title">
              {deleted ? '恢复' : '删除'}选中的 {selectedOrders.length} 个订单？
            </h2>
            <p>
              {deleted
                ? '订单将重新显示在工作列表中，原有状态不变。'
                : '订单将移入“已删除”，不会清除支付、客户或审计记录。批次中任一订单状态改变，本次操作将全部取消。'}
            </p>
            <label>
              操作原因（必填）
              <textarea
                ref={firstInput}
                maxLength={200}
                value={reason}
                disabled={busy}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
            {feedback && (
              <p role="alert" className="order-feedback error">
                {feedback}
              </p>
            )}
            <footer>
              <button type="button" disabled={busy} onClick={close}>
                取消
              </button>
              <button
                type="button"
                className="order-danger"
                disabled={busy || !reason.trim()}
                onClick={() => void submit()}
              >
                {busy ? '处理中…' : `确认${deleted ? '恢复' : '删除'}`}
              </button>
            </footer>
          </section>
        </div>
      )}
    </>
  );
}
